// Default class names for loader container
export const SELECTOR = '[data-image-loader="true"]';

class ProgressiveImageLoader {
  /**
   * Initializer
   *
   * @param {Element} elementRef - HTML node, to progressively load a background image.
   * */
  constructor(elementRef) {
    // Initialise attributes
    this.elementRef = elementRef;
    this.elementRef.classList.add('progressive-image');
    this.image = new Image();
    this.retryCount = 0;

    // Bind functions
    this.onLoad = this.onLoad.bind(this);
    this.onError = this.onError.bind(this);

    this._setup();
  }

  /**
   * Load the high res image.
   * */
  load() {
    if (this.started || this.observer) {
      return;
    }
    if (this.elementRef.dataset['imageLazy'] === 'true' && window.IntersectionObserver) {
      this.observer = new window.IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.observer.disconnect();
          this._loadImage();
        }
      }, { rootMargin: '200px' });
      this.observer.observe(this.elementRef);
      return;
    }
    this._loadImage();
  }

  _loadImage() {
    this.started = true;
    this.image.src = this.elementRef.dataset['imagePath'];
  }

  // Event Handlers

  /**
   * Event callback to be triggered after the high res image has been loaded.
   * */
  onLoad() {
    window.clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.overlay.style.backgroundImage = `url(${this.image.src})`;
    this.overlay.style.opacity = 1;
  }

  onError() {
    if (this.retryCount >= 2 || this.retryTimer) {
      return;
    }
    this.retryCount += 1;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      if (this.elementRef.isConnected) {
        this._loadImage();
      }
    }, this.retryCount * 1000);
  }

  // private

  /**
   * Set-up the ProgressiveImageLoader.
   *  1) Append the overlay element to load the high res image.
   * */
  _setup() {
    this.overlay = this._createOverlay();
    this._addEventListeners();
  }

  /**
   * Creates the overlay.
   *
   * @return {Element} - HTML node for the overlay.
   * */
  _createOverlay() {
    let overlay = document.createElement('div');
    overlay.setAttribute('class', 'progressive-image--overlay');
    this.elementRef.appendChild(overlay);

    return overlay;
  }

  /**
   * Adds all the required event listeners.
   * */
  _addEventListeners() {
    this.image.onload = this.onLoad;
    this.image.onerror = this.onError;
  }
}

export default ProgressiveImageLoader;
